import {
  Button,
  Col,
  Form,
  Input,
  PageHeader,
  Result,
  Row,
  Select,
  Spin,
  message,
} from "antd"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMsal } from "@azure/msal-react"
import { useNavigate } from "react-router"

import { loginRequest } from "../../config/api.config"
import HeaderFarms from "../../utils/header_farms"
import Footer from "../../atoms/footer"
import API from "../../services"
import id_png from "./id.png"
import "./style.css"

const { Option } = Select

const PAGE_STATE = {
  LOADING: "loading",
  FORM: "form",
  SUBMITTING: "submitting",
  ERROR: "error",
}

const buildErrorMessage = error => {
  const responseMessage =
    error?.response?.data?.error?.[0]?.name ||
    error?.response?.data?.message ||
    error?.response?.data?.error

  if (responseMessage) {
    return String(responseMessage)
  }

  return "Terjadi kesalahan saat menyiapkan akun approval."
}

const normalizeText = value =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()

const mapGraphProfile = profile => ({
  fullname: profile?.displayName || "",
  email: profile?.mail || profile?.userPrincipalName || "",
  employeeId: profile?.employeeId || "",
  phone_number: profile?.mobilePhone || "",
})

const findLevelId = (levels, levelName) => {
  const normalizedLevel = normalizeText(levelName)
  return levels.find(level => normalizeText(level.fullname) === normalizedLevel)?._id
}

const findRoleId = roles => roles.find(role => role.name === "user")?._id || roles[0]?._id

const buildInitialValues = ({ talenta, graphProfile, companyId, departmentId, levelUserId, roleId, supervisorNik }) => ({
  fullname: String(talenta?.fullname || graphProfile?.fullname || "").replace(/\s+/g, " ").trim(),
  employeeId: talenta?.employee_id || graphProfile?.employeeId || "",
  email: graphProfile?.email || talenta?.email || "",
  phone_number: talenta?.mobile_phone || graphProfile?.phone_number || "",
  position: talenta?.job_position || "",
  direct_spv: supervisorNik || undefined,
  company: companyId || undefined,
  department: departmentId || undefined,
  roles: roleId || undefined,
  level_user: levelUserId || undefined,
})

const requiresManualCompletion = values =>
  !values.fullname ||
  !values.employeeId ||
  !values.email ||
  !values.position ||
  !values.company ||
  !values.department ||
  !values.roles

function CompletedUserData() {
  const navigate = useNavigate()
  const { instance, accounts } = useMsal()
  const [form] = Form.useForm()
  const initializedRef = useRef(false)

  const currentAccount = accounts[0]

  const [pageState, setPageState] = useState(PAGE_STATE.LOADING)
  const [errorMessage, setErrorMessage] = useState("")
  const [companies, setCompanies] = useState([])
  const [departments, setDepartments] = useState([])
  const [roles, setRoles] = useState([])
  const [levels, setLevels] = useState([])
  const [supervisors, setSupervisors] = useState([])

  const selectedCompany = Form.useWatch("company", form)

  const getAccessToken = useCallback(async () => {
    if (!currentAccount) {
      throw new Error("Microsoft account is not available")
    }

    const tokenResponse = await instance.acquireTokenSilent({
      ...loginRequest,
      account: currentAccount,
    })

    return tokenResponse.accessToken
  }, [currentAccount, instance])

  const getGraphProfile = useCallback(async accessToken => {
    const response = await fetch(
      "https://graph.microsoft.com/v1.0/me?$select=displayName,mail,userPrincipalName,employeeId,mobilePhone",
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    if (!response.ok) {
      throw new Error("Failed to load Microsoft profile")
    }

    return response.json()
  }, [])

  const getDepartmentOptions = useCallback(
    async (accessToken, companyId) => {
      if (!companyId) {
        setDepartments([])
        return []
      }

      const response = await API.getDepartmentsByCompanyApproval(
        HeaderFarms(accessToken),
        companyId
      )

      const nextDepartments = Array.isArray(response?.data) ? response.data : []
      setDepartments(nextDepartments)

      return nextDepartments
    },
    []
  )

  const persistApprovalSession = useCallback(async accessToken => {
    const sessionFarms = await API.getSessionSignin(HeaderFarms(accessToken))
    localStorage.setItem("session_farms", JSON.stringify(sessionFarms))
  }, [])

  const submitCompletedUser = useCallback(
    async (values, providedToken) => {
      const accessToken = providedToken || (await getAccessToken())

      setPageState(PAGE_STATE.SUBMITTING)

      try {
        await API.completeUserData(HeaderFarms(accessToken), values)
        await persistApprovalSession(accessToken)

        const selectedCompanyOption = companies.find(
          company => company._id === values.company
        )
        const selectedSupervisor = supervisors.find(
          user => String(user.nik) === String(values.direct_spv)
        )

        localStorage.setItem(
          "session_signin",
          JSON.stringify({
            nik: values.employeeId,
            fullname: values.fullname,
            email: values.email,
            phone_number: values.phone_number || null,
            phone_number_activation: Boolean(values.phone_number),
            company: selectedCompanyOption
              ? {
                  _id: selectedCompanyOption._id,
                  fullname: selectedCompanyOption.fullname,
                  code: selectedCompanyOption.code,
                }
              : null,
            direct_supervisor: selectedSupervisor ? [selectedSupervisor] : [],
          })
        )

        setPageState(PAGE_STATE.FORM)
        message.success("Akun approval berhasil disiapkan")
        navigate("/home", { replace: true })
      } catch (error) {
        setPageState(PAGE_STATE.FORM)
        message.warning(buildErrorMessage(error))
      }
    },
    [companies, getAccessToken, navigate, persistApprovalSession, supervisors]
  )

  const initializeForm = useCallback(async () => {
    const accessToken = await getAccessToken()
    const graphResponse = await getGraphProfile(accessToken)
    const graphProfile = mapGraphProfile(graphResponse)

    if (!graphProfile.employeeId) {
      throw new Error("Employee ID tidak ditemukan di Microsoft 365")
    }

    const [talentaResponse, companiesResponse, levelsResponse, rolesResponse, usersResponse] =
      await Promise.all([
        API.ApprovalGetEmployeeDetail(HeaderFarms(accessToken), graphProfile.employeeId),
        API.getCompaniesApproval(HeaderFarms(accessToken), { active: true }),
        API.getUserLevelsApproval(HeaderFarms(accessToken)),
        API.getRolesApproval(HeaderFarms(accessToken)),
        API.getMasterUsers(HeaderFarms(accessToken)),
      ])

    const talenta = talentaResponse?.data || {}
    const companyOptions = Array.isArray(companiesResponse?.data)
      ? companiesResponse.data
      : []
    const levelOptions = Array.isArray(levelsResponse?.data)
      ? levelsResponse.data
      : []
    const roleOptions = Array.isArray(rolesResponse?.data)
      ? rolesResponse.data
      : []
    const userOptions = Array.isArray(usersResponse?.data)
      ? usersResponse.data
      : []

    setCompanies(companyOptions)
    setLevels(levelOptions)
    setRoles(roleOptions)
    setSupervisors(userOptions)

    const mappedCompanyId =
      companyOptions.find(company => company._id === talenta.branch_id)?._id ||
      companyOptions.find(
        company => normalizeText(company.code) === normalizeText(talenta.branch_nickname)
      )?._id

    const departmentOptions = await getDepartmentOptions(accessToken, mappedCompanyId)
    const mappedDepartmentId = departmentOptions.find(
      department =>
        normalizeText(department.fullname) === normalizeText(talenta.organization_name) ||
        normalizeText(department.code) === normalizeText(talenta.organization_name)
    )?._id

    const mappedSupervisorNik = userOptions.find(
      user => String(user.nik) === String(talenta.approval_line_employee_id)
    )?.nik

    const initialValues = buildInitialValues({
      talenta,
      graphProfile,
      companyId: mappedCompanyId,
      departmentId: mappedDepartmentId,
      levelUserId: findLevelId(levelOptions, talenta.job_level),
      roleId: findRoleId(roleOptions),
      supervisorNik: mappedSupervisorNik,
    })

    form.setFieldsValue(initialValues)

    if (requiresManualCompletion(initialValues)) {
      setPageState(PAGE_STATE.FORM)
      return
    }

    await submitCompletedUser(initialValues, accessToken)
  }, [form, getAccessToken, getDepartmentOptions, getGraphProfile, submitCompletedUser])

  const handleFinish = useCallback(
    async values => {
      await submitCompletedUser(values)
    },
    [submitCompletedUser]
  )

  useEffect(() => {
    if (!currentAccount) {
      instance.loginRedirect(loginRequest).catch(() => {
        message.warning("Gagal mengarahkan ke login Microsoft 365")
      })
      return
    }

    if (initializedRef.current) {
      return
    }

    initializedRef.current = true

    void initializeForm().catch(error => {
      setErrorMessage(buildErrorMessage(error))
      setPageState(PAGE_STATE.ERROR)
    })
  }, [currentAccount, initializeForm, instance])

  useEffect(() => {
    if (!selectedCompany || pageState !== PAGE_STATE.FORM) {
      return
    }

    void (async () => {
      try {
        const accessToken = await getAccessToken()
        const nextDepartments = await getDepartmentOptions(accessToken, selectedCompany)
        const currentDepartment = form.getFieldValue("department")

        if (
          currentDepartment &&
          !nextDepartments.some(department => department._id === currentDepartment)
        ) {
          form.setFieldsValue({ department: undefined })
        }
      } catch (error) {
        message.warning(buildErrorMessage(error))
      }
    })()
  }, [form, getAccessToken, getDepartmentOptions, pageState, selectedCompany])

  const pageTitle = useMemo(() => {
    if (pageState === PAGE_STATE.SUBMITTING || pageState === PAGE_STATE.LOADING) {
      return "Menyiapkan akun approval anda"
    }

    if (pageState === PAGE_STATE.ERROR) {
      return "Akun approval belum berhasil disiapkan"
    }

    return "Lengkapi data akun approval"
  }, [pageState])

  return (
    <>
      <div className="container" style={{ minHeight: "700px" }}>
        <div className="row">
          <div className="col-md-12">
            <PageHeader ghost={true} title="User Register" />
          </div>
        </div>

        {(pageState === PAGE_STATE.LOADING || pageState === PAGE_STATE.SUBMITTING) && (
          <Result
            status="info"
            icon={<Spin size="large" />}
            title={pageTitle}
            subTitle="Sedang mengambil data Microsoft 365, Talenta, dan approval master."
          />
        )}

        {pageState === PAGE_STATE.ERROR && (
          <Result
            status="error"
            icon={<img src={id_png} width="120px" alt="" />}
            title={pageTitle}
            subTitle={errorMessage}
            extra={
              <div className="d-flex justify-content-center gap-2">
                <Button
                  type="primary"
                  onClick={() => {
                    initializedRef.current = false
                    setPageState(PAGE_STATE.LOADING)
                    setErrorMessage("")
                    void initializeForm().catch(error => {
                      setErrorMessage(buildErrorMessage(error))
                      setPageState(PAGE_STATE.ERROR)
                    })
                  }}
                >
                  Retry
                </Button>
                <Button onClick={() => navigate("/", { replace: true })}>
                  Back to Login
                </Button>
              </div>
            }
          />
        )}

        {pageState === PAGE_STATE.FORM && (
          <div className="row">
            <div className="col-md-12">
              <Result
                status="info"
                icon={<img src={id_png} width="120px" alt="" />}
                title="Lengkapi mapping user approval"
                subTitle="Data dasar sudah diisi otomatis. Lengkapi field yang belum termap lalu submit."
                extra={
                  <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleFinish}
                    onFinishFailed={() => message.warning("Mohon lengkapi formulir.")}
                  >
                    <Row justify="center">
                      <Col xs={24} sm={22} md={18} lg={14} xl={12}>
                        <Form.Item
                          label="Fullname"
                          name="fullname"
                          rules={[{ required: true, message: "Please input fullname" }]}
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item
                          label="Employee ID"
                          name="employeeId"
                          rules={[{ required: true, message: "Please input employee ID" }]}
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item
                          label="Email Company"
                          name="email"
                          rules={[{ required: true, message: "Please input email company" }]}
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item label="Whatsapp Number" name="phone_number">
                          <Input />
                        </Form.Item>

                        <Form.Item
                          label="Position"
                          name="position"
                          rules={[{ required: true, message: "Please input position" }]}
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item label="Direct Supervisor" name="direct_spv">
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder="Select direct supervisor"
                          >
                            {supervisors.map(user => (
                              <Option key={user._id} value={user.nik}>
                                {`${user.fullname} (${user.nik})`}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          label="Unit"
                          name="company"
                          rules={[{ required: true, message: "Please select unit" }]}
                        >
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder="Select company"
                          >
                            {companies.map(company => (
                              <Option key={company._id} value={company._id}>
                                {company.fullname}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          label="Department"
                          name="department"
                          rules={[{ required: true, message: "Please select department" }]}
                        >
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder={selectedCompany ? "Select department" : "Select company first"}
                            disabled={!selectedCompany}
                          >
                            {departments.map(department => (
                              <Option key={department._id} value={department._id}>
                                {department.fullname}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          label="Roles"
                          name="roles"
                          rules={[{ required: true, message: "Please select role" }]}
                        >
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder="Select role"
                          >
                            {roles.map(role => (
                              <Option key={role._id} value={role._id}>
                                {role.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item label="User Level" name="level_user">
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder="Select user level (optional)"
                          >
                            {levels.map(level => (
                              <Option key={level._id} value={level._id}>
                                {level.fullname}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row className="justify-content-center">
                      <Col>
                        <Form.Item>
                          <Button type="primary" htmlType="submit">
                            Submit
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                }
              />
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  )
}

export default CompletedUserData
